/**
 * Utility functions for managing dynamic page titles
 */

/**
 * Converts a path to proper title case
 * @param {string} path - The current route path
 * @returns {string} - The title case title
 */
export const pathToTitleCase = (path) => {
    // Remove leading slash and split by slashes
    const segments = path.replace(/^\//, "").split("/").filter(Boolean);

    if(segments.length === 0) {
        return "AQI Home";
    }

    // Convert each segment to title case
    return segments
        .map((segment) => {
            // Remove hyphens and convert to title case
            const words = segment.split(/[-_]/).filter(Boolean);

            return words
                .map(
                    (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase(),
                )
                .join(" ");
        })
        .join(" ");
};

/**
 * Custom title mappings for specific routes
 */
const customTitles = {
    "/": "Home",
    "/login": "Login",
    "/sign-up": "Sign Up",
    "/dashboard": "Dashboard",
    "/investor-dashboard": "Investor Dashboard",
    "/entrepreneur-dashboard": "Entrepreneur Dashboard",
    "/entrepreneur-questions": "Campaign Questions",
    "/admin-dashboard": "Admin Dashboard",
    "/forgot-password": "Forgot Password",
    "/forgot-username": "Forgot Username",
    "/reset-password": "Reset Password",
    "/reset-passwordPage": "Reset Password Page",
    "/verify-emailPassword": "Verify Email Password",
    "/investor-application": "Investor Application",
    "/investor-welcome": "Investor Welcome",
    "/phone-verification": "Phone Verification",
    "/investment-limits": "Investment Information",
    "/entrepreneur-application": "Entrepreneur Application",
    "/entrepreneur-requirements": "Entrepreneur Requirements",
    "/entrepreneur-terms": "Entrepreneur Terms",
    "/investor-account-creation": "Investor Account Creation",
    "/entrepreneur-account-creation": "Entrepreneur Account Creation",
    "/investor-consent": "Investor Consent",
    "/payment-success": "Payment Success",
    "/404": "Page Not Found",
    "/access-denied": "Access Denied",
    "/unauthorized": "Unauthorized",
    "/faq-public": "FAQ Public",
    "/contact-us": "Contact Us",
    "/subscribe-alerts": "Subscribe Alerts",
    "/oauth-complete": "OAuth Complete",
    "/mission": "Mission",
    "/news": "News",
    "/investors": "Investors",
    "/entrepreneurs": "Entrepreneurs",
    "/resources": "Resources",
    "/Menu-items": "Menu Items",
    "/Security-groups": "Security Groups",
    "/Chat-logs": "Chat Logs",
    "/User-sessions": "User Sessions",
    "/Users": "Users",
    "/create-user": "Create User",
    "/investments": "Investments",
    "/Investor-tracker": "Investor Tracker",
    "/investor-review": "Investor Review",
    "/invest": "Invest",
    "/Campaign-tracker": "Campaign Tracker",
    "/campaign-page": "Campaign Page",
    "/chat": "Chat",
    "/faq": "FAQ",
    "/contact": "Contact",
    "/entrepreneur-review-dashboard": "Entrepreneur Review Dashboard",
    "/entrepreneur-faq": "Entrepreneur FAQ",
    "/support": "Support",
    "/Account": "Account",
    "/Documents-center": "Documents Center",
    "/north-capital": "North Capital",
    "/Security-log": "Security Log",
    "/Change-log": "Change Log",
    "/Mail-queue": "Mail Queue",
    "/FAQ-management": "FAQ Management",
    "/SEO-management": "SEO Configuration",
    "/BlogPost-management": "News & Blogs",
    "/Affiliates-management": "Affiliates",
    "/applications": "Applications",
    "/campaigns": "Campaigns",
    "/subscribers": "Subscribers",
};

/**
 * Gets the title case title for a given path
 * @param {string} path - The current route path
 * @returns {string} - The title case title
 */
export const getPageTitle = (path) => {
    // Check for custom mappings first
    if(customTitles[path]) {
        return customTitles[path];
    }

    // Handle dynamic routes with parameters
    if(path.includes("/campaigns/view/")) {
        return "Campaign View";
    }

    if(path.includes("/campaigns/")) {
        return "Campaign Details";
    }

    if(path.includes("/chat-history/")) {
        return "Chat History";
    }

    if(path.includes("/addpeople/")) {
        return "Add People";
    }

    if(path.includes("/addpages/")) {
        return "Add Pages";
    }

    if(path.includes("/blog/")) {
        return "Blog Post";
    }

    if(path.includes("/admin/create-menu")) {
        return "Admin Create Menu";
    }

    if(path.includes("/admin/order-menuitems")) {
        return "Admin Order Menu Items";
    }

    // Handle FAQ sub-routes
    if(path.includes("/faq/public/")) {
        const segment = path.split("/").pop();

        if(segment === "investors") return "Investor FAQ";
        if(segment === "entrepreneurs") return "Entrepreneur FAQ";
        if(segment === "glossary") return "Glossary";
    }

    // Fall back to automatic conversion
    return pathToTitleCase(path);
};

/**
 * Sets the document title with the AQI prefix
 * @param {string} pageTitle - The title case page title
 */
export const setDocumentTitle = (pageTitle) => {
    document.title = `${pageTitle} | AQi`;
};

/**
 * Hook-like function to update title based on current location
 * @param {string} pathname - The current pathname from useLocation
 */
export const updatePageTitle = (pathname) => {
    const title = getPageTitle(pathname);

    setDocumentTitle(title);

    return title;
};
